/**
 * Mat3 仿射工具（M2-14 / DESIGN.md §5.3）。
 *
 * 与文档模型的 {@link Mat3}（CSS/Canvas 的 a,b,c,d,e,f）同一约定：
 * ```
 * | a c e |   x' = a·x + c·y + e
 * | b d f |   y' = b·x + d·y + f
 * ```
 * 全部纯函数。`invert` 在这里存在是为了逆映射采样；**命令的逆操作
 * 不要用它**——退化矩阵没有逆，命令逆操作应存旧矩阵（M1-07 的约定）。
 */
/**
 * 仿射矩阵（与文档层的 Mat3 结构等价——分层规则不允许 engine 引 document，
 * TS 的结构类型让两边无缝互通）。
 */
export interface Mat3 {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

export const IDENTITY: Mat3 = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** m2 ∘ m1（先应用 m1，再 m2）。 */
export function multiply(m2: Mat3, m1: Mat3): Mat3 {
  return {
    a: m2.a * m1.a + m2.c * m1.b,
    b: m2.b * m1.a + m2.d * m1.b,
    c: m2.a * m1.c + m2.c * m1.d,
    d: m2.b * m1.c + m2.d * m1.d,
    e: m2.a * m1.e + m2.c * m1.f + m2.e,
    f: m2.b * m1.e + m2.d * m1.f + m2.f,
  };
}

export function translation(tx: number, ty: number): Mat3 {
  return { a: 1, b: 0, c: 0, d: 1, e: tx, f: ty };
}

export function scaling(sx: number, sy: number): Mat3 {
  return { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
}

export function rotation(radians: number): Mat3 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
}

/** 斜切。shx：x 随 y 偏移；shy：y 随 x 偏移。 */
export function shearing(shx: number, shy: number): Mat3 {
  return { a: 1, b: shy, c: shx, d: 1, e: 0, f: 0 };
}

/** 绕锚点应用变换：T(anchor) · m · T(−anchor)。 */
export function aboutAnchor(m: Mat3, anchor: { x: number; y: number }): Mat3 {
  return multiply(translation(anchor.x, anchor.y), multiply(m, translation(-anchor.x, -anchor.y)));
}

export function applyTo(m: Mat3, p: { x: number; y: number }): { x: number; y: number } {
  return { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
}

/** 行列式。0 = 退化（面积塌缩）。 */
export function determinant(m: Mat3): number {
  return m.a * m.d - m.b * m.c;
}

/** 逆矩阵。退化时返回 null——调用方必须处理，静默给单位阵会画出鬼图。 */
export function invert(m: Mat3): Mat3 | null {
  const det = determinant(m);
  if (det === 0 || !Number.isFinite(det)) return null;
  const inv = 1 / det;
  return {
    a: m.d * inv,
    b: -m.b * inv,
    c: -m.c * inv,
    d: m.a * inv,
    e: (m.c * m.f - m.d * m.e) * inv,
    f: (m.b * m.e - m.a * m.f) * inv,
  };
}

/** 是否轴对齐（无旋转/斜切），提交时据此选 Lanczos 或双线性路径。 */
export function isAxisAligned(m: Mat3, epsilon = 1e-6): boolean {
  return Math.abs(m.b) < epsilon && Math.abs(m.c) < epsilon;
}
